import { create } from "zustand";
import type {
  LayoutType,
  BrandConfig,
  TenantConfig,
  TenantProfile,
  BrandId,
  TenantCode,
} from "../config/env.js";
import {
  getBrandConfigByBrandId,
  isRuntimeTenantPreviewHost,
  mapRuntimeTenantSourceToTenantCode,
  resolveAuthLayoutFromCallbackTarget,
  resolveDomainProfile,
} from "../config/env.js";

// 动态导入品牌资源（避免循环依赖）
let getBrandAssetsFunc: ((brandId?: BrandId) => { logo: string; solidLogo: string; favicon: string }) | null = null;

/**
 * 注入品牌资源获取函数
 * 由应用在启动时调用，传入 @ifa/ui 的 getBrandAssets 函数
 */
export function setBrandAssetsProvider(
  provider: (brandId?: BrandId) => { logo: string; solidLogo: string; favicon: string }
) {
  getBrandAssetsFunc = provider;
}

/**
 * 合并品牌配置和品牌资源
 */
function mergeBrandConfigWithAssets(
  config: BrandConfig,
  brandId?: BrandId
): BrandConfig {
  if (getBrandAssetsFunc) {
    const assets = getBrandAssetsFunc(brandId);
    return {
      ...config,
      logo: assets.logo,
      solidLogo: assets.solidLogo,
      favicon: assets.favicon,
    };
  }
  return config;
}

interface ConfigState {
  // 标准化租户档案
  tenantProfile: TenantProfile | null;
  // 品牌标识
  brandId: BrandId;
  // 布局类型
  layoutType: LayoutType;
  // 品牌配置
  brandConfig: BrandConfig;
  // 租户配置
  tenantConfig: {
    config: TenantConfig | null;
    isGenericSSO: boolean;
    tenantResolutionMode: TenantConfig["tenantResolutionMode"] | null;
    requireManualTenantInput: boolean;
  };
  runtimeTenantCode: TenantCode | null;
  // 是否已初始化
  initialized: boolean;

  // 初始化配置（基于 callback URL，用于 SSO 登录页）
  initializeConfig: (callbackUrl: string) => void;
  // 初始化配置（基于当前域名，用于业务应用）
  initializeFromCurrentDomain: () => void;
  setRuntimeTenantCode: (tenantCode: TenantCode | null) => void;
  syncRuntimeTenantWithCurrentDomain: (tenantSource?: string | null) => void;
  syncRuntimeTenantWithCallback: (
    callbackUrl: string,
    tenantSource?: string | null,
  ) => void;
  // 更新布局类型
  setLayoutType: (layoutType: LayoutType) => void;
  // 更新当前租户品牌配置
  updateBrandConfig: () => void;
}

const getInitialDomainProfile = (): TenantProfile =>
  resolveDomainProfile({
    source: "domain",
    value: typeof window !== "undefined" ? window.location.host : "",
  });

const initialDomainProfile = getInitialDomainProfile();

const buildConfigStateFromProfile = (profile: TenantProfile) => {
  const brandConfig = mergeBrandConfigWithAssets(profile.brandConfig, profile.brandId);
  const tenantProfile = {
    ...profile,
    brandConfig,
  };

  return {
    tenantProfile,
    brandId: profile.brandId,
    layoutType: profile.layoutType,
    brandConfig,
    tenantConfig: {
      config: profile.tenantConfig,
      isGenericSSO: profile.isGenericSSO,
      tenantResolutionMode: profile.tenantResolutionMode,
      requireManualTenantInput: profile.requireManualTenantInput,
    },
  };
};

type DerivedConfigState = ReturnType<typeof buildConfigStateFromProfile>;

const updateProfileAndDerivedState = (
  profile: TenantProfile,
  overrides?: Partial<DerivedConfigState> & Pick<ConfigState, "initialized">
) => ({
  ...buildConfigStateFromProfile(profile),
  ...overrides,
});

const initialStateFromProfile = buildConfigStateFromProfile(initialDomainProfile);

const resolveRuntimeTenantCodeForHostname = (
  tenantSource?: string | null,
  hostname?: string,
): TenantCode | null => {
  if (!isRuntimeTenantPreviewHost(hostname)) {
    return null;
  }

  return mapRuntimeTenantSourceToTenantCode(tenantSource);
};

export const useConfigStore = create<ConfigState>((set, get) => ({
  ...initialStateFromProfile,
  runtimeTenantCode: null,
  initialized: false,

  initializeConfig: (callbackUrl: string) => {
    const authHostname = typeof window !== "undefined" ? window.location.host : "";
    const runtimeTenantCode = get().runtimeTenantCode;
    const authProfile = resolveDomainProfile(
      { source: "domain", value: authHostname },
      { runtimeTenantCode },
    );
    const callbackLayout = resolveAuthLayoutFromCallbackTarget(callbackUrl);
    const normalizedState = buildConfigStateFromProfile(authProfile);

    set({
      ...normalizedState,
      layoutType: callbackLayout.layoutType,
      initialized: true,
    });

    console.log("[ConfigStore] 配置已初始化（基于 callback URL）:", {
      authHostname: authProfile.hostname,
      callbackHostname: callbackLayout.hostname,
      layoutType: callbackLayout.layoutType,
      brandConfig: normalizedState.brandConfig.name,
      tenantCode: normalizedState.tenantConfig.config?.tenantCode,
    });
  },

  initializeFromCurrentDomain: () => {
    const hostname = typeof window !== "undefined" ? window.location.host : "";
    const runtimeTenantCode = get().runtimeTenantCode;
    const profile = resolveDomainProfile(
      { source: "domain", value: hostname },
      { runtimeTenantCode },
    );
    const normalizedState = buildConfigStateFromProfile(profile);

    set({
      ...normalizedState,
      initialized: true,
    });

    console.log("[ConfigStore] 配置已初始化（基于当前域名）:", {
      hostname: profile.hostname,
      layoutType: profile.layoutType,
      brandConfig: normalizedState.brandConfig.name,
      tenantCode: normalizedState.tenantConfig.config?.tenantCode,
    });
  },

  setRuntimeTenantCode: (runtimeTenantCode: TenantCode | null) => {
    set({ runtimeTenantCode });
  },

  syncRuntimeTenantWithCurrentDomain: (tenantSource) => {
    const hostname = typeof window !== "undefined" ? window.location.host : "";
    const runtimeTenantCode = resolveRuntimeTenantCodeForHostname(
      tenantSource,
      hostname,
    );

    set({ runtimeTenantCode });
    get().initializeFromCurrentDomain();
  },

  syncRuntimeTenantWithCallback: (callbackUrl, tenantSource) => {
    const hostname = typeof window !== "undefined" ? window.location.host : "";
    const runtimeTenantCode = resolveRuntimeTenantCodeForHostname(
      tenantSource,
      hostname,
    );

    set({ runtimeTenantCode });
    get().initializeConfig(callbackUrl);
  },

  setLayoutType: (layoutType: LayoutType) => {
    set((state) => {
      if (!state.tenantProfile) {
        return { layoutType };
      }

      const nextProfile = {
        ...state.tenantProfile,
        layoutType,
      };

      return updateProfileAndDerivedState(nextProfile, {
        initialized: state.initialized,
      });
    });
  },

  updateBrandConfig: () => {
    const profile = get().tenantProfile;
    if (!profile) {
      return;
    }

    const nextProfile = {
      ...profile,
      brandConfig: getBrandConfigByBrandId(profile.brandId),
    };

    set((state) =>
      updateProfileAndDerivedState(nextProfile, {
        initialized: state.initialized,
      })
    );
  },
}));
